import { useEffect, useMemo, useState } from "react"
import Icon from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"
import UptimeCategoryNav from "@/v2-backoffice-client/uptime/UptimeCategoryNav"
import UptimeCategoryTable from "@/v2-backoffice-client/uptime/UptimeCategoryTable"
import UptimeOverview from "@/v2-backoffice-client/uptime/UptimeOverview"
import UptimeIncidentTable, {
  UPTIME_INCIDENT_TABLE_ANCHOR_ID,
} from "@/v2-backoffice-client/uptime/incidents/UptimeIncidentTable"
import { useUptimeQuery } from "@/v2-backoffice-client/uptime/uptime.query"
import { buildUptimeBoardData } from "@/v2-backoffice-client/uptime/uptime.buckets.utils"

function UptimeBoard() {
  const { data, previousData, loading, error, refetch } = useUptimeQuery({
    notifyOnNetworkStatusChange: true,
    pollInterval: 60_000,
  })

  // Start on the incidents view; auto-switch to the first category
  // once data loads if there are no active issues.
  const [selectedCategory, setSelectedCategory] = useState<string>(
    UPTIME_INCIDENT_TABLE_ANCHOR_ID,
  )

  const displayData = data ?? previousData
  const targets = displayData?.getUptimeTargets ?? []
  const overview = displayData?.getUptimeOverview ?? null

  // Single fleet-wide pass per poll cycle:
  //   - sidebar groups (with pre-computed stats per category),
  //   - attention counts (down/slow/drift across the active fleet)
  //     for the sidebar's "Active incidents" pseudo-item,
  //   - bucket counts (all/down/slow/drift/muted) for the incident
  //     table's segmented pills.
  // Every aggregate skips muted rows; the headline numbers all
  // come from the backend's already-mute-aware overview row, so
  // the page never represents silenced targets in fleet stats.
  // Before this consolidation each consumer ran its own filter +
  // count pass on every poll, doubling the work for what is by far
  // the hottest data path on the page.
  const board = useMemo(() => buildUptimeBoardData(targets), [targets])

  // Once data arrives, fall through from "incidents" to the first
  // category when nothing needs attention. Drift counts here so an
  // operator who lands on the page sees the drift panel even when
  // every service is up -- the whole point of incident management
  // is that "configuration changed" is also an incident.
  useEffect(() => {
    if (loading || selectedCategory !== UPTIME_INCIDENT_TABLE_ANCHOR_ID) return
    const { down, slow, drift } = board.attentionCounts
    const hasAttention = down > 0 || slow > 0 || drift > 0
    if (!hasAttention) {
      // Auto-fall-through to the first category of the first
      // platform that actually has a group to render.
      for (const platform of board.byPlatform) {
        if (platform.groups.length > 0) {
          setSelectedCategory(platform.groups[0].groupKey)
          return
        }
      }
    }
  }, [loading, board, selectedCategory])

  const isIncidents = selectedCategory === UPTIME_INCIDENT_TABLE_ANCHOR_ID
  const selectedGroupKey = isIncidents ? null : selectedCategory
  const selectedGroup = selectedGroupKey
    ? board.groupByKey.get(selectedGroupKey)
    : undefined

  return (
    <Tooltip.Provider delayDuration={150}>
      {/* Global KPI strip: always visible. The KPI strip's chart
          helpers and status breakdown all skip muted rows
          internally, so we hand them the raw fleet -- no caller-
          side filter to keep in sync. */}
      <UptimeOverview
        overview={overview}
        isLoading={loading && !displayData}
        targets={targets}
        driftCount={board.attentionCounts.drift}
        onIncidentsClick={() =>
          setSelectedCategory(UPTIME_INCIDENT_TABLE_ANCHOR_ID)
        }
        onDriftClick={() =>
          setSelectedCategory(UPTIME_INCIDENT_TABLE_ANCHOR_ID)
        }
      />

      {error && (
        <div className="text-sm text-destructive flex items-center gap-2">
          <Icon src="triangle-warning" />
          Failed to load uptime data: {error.message}
          <button
            type="button"
            className="underline decoration-dotted ml-1"
            onClick={() => {
              refetch()
            }}
          >
            retry
          </button>
        </div>
      )}

      {!error && !loading && targets.length === 0 && (
        <div className="text-sm text-muted-foreground">
          No monitored targets yet.
        </div>
      )}

      {/* Two-column layout: sidebar selects; right panel replaces. */}
      {(targets.length > 0 || (loading && !displayData)) && (
        <div className="uptime-board-split">
          <UptimeCategoryNav
            platforms={board.byPlatform}
            attentionCounts={board.attentionCounts}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
          />

          {isIncidents && <UptimeIncidentTable />}

          {!isIncidents && selectedGroup && (
            <UptimeCategoryTable
              groupKey={selectedGroup.groupKey}
              source={selectedGroup.source}
              platform={selectedGroup.platform}
              summary={selectedGroup.stats}
            />
          )}

          {!isIncidents && !selectedGroup && !loading && (
            <div className="text-sm text-muted-foreground text-center py-10">
              Select a category from the sidebar.
            </div>
          )}
        </div>
      )}
    </Tooltip.Provider>
  )
}

export default UptimeBoard
